import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OAuthAccountDTO, OAuthProvider } from "../../shared/schemas/oauth.schema";
import { apiFetch } from "./client";

const OAUTH_ACCOUNTS_KEY = ["oauth-accounts"] as const;

export function useOAuthAccounts() {
  return useQuery<OAuthAccountDTO[]>({
    queryKey: OAUTH_ACCOUNTS_KEY,
    queryFn: () => apiFetch<OAuthAccountDTO[]>("/oauth/accounts"),
  });
}

export function useUnlinkOAuth() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (provider: OAuthProvider) =>
      apiFetch<{ success: boolean }>(`/oauth/${provider}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: OAUTH_ACCOUNTS_KEY });
    },
  });
}

export function useSetPassword() {
  return useMutation({
    mutationFn: (data: { password: string }) =>
      apiFetch<{ recoveryKey: string }>("/users/me/set-password", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  });
}

export function getOAuthInitUrl(provider: OAuthProvider, action: "login" | "link" = "login"): string {
  const base = import.meta.env.VITE_API_URL ?? "/api";
  const params = new URLSearchParams();
  if (action === "link") {
    params.set("action", "link");
  }
  const query = params.toString();
  const path = `${base}/oauth/${provider}${query ? `?${query}` : ""}`;
  return path;
}

/**
 * URL OAuth complète pour le mode natif (Capacitor).
 * Pointe vers le serveur de prod avec `platform=native` pour que
 * le callback redirige vers palnia:// au lieu de https://.
 */
export function getNativeOAuthUrl(provider: OAuthProvider, action: "login" | "link" = "login"): string {
  const apiBase = import.meta.env.VITE_API_URL ?? "https://palnia.newalfox.fr/api";
  const params = new URLSearchParams({ platform: "native" });
  if (action === "link") {
    params.set("action", "link");
  }
  return `${apiBase}/oauth/${provider}?${params.toString()}`;
}
